import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";
import BulkGreetingsModal from "./BulkGreetingsModal";

/**
 * HolidayPromptPopup — banner discreto in basso a destra che ricorda
 * all'agente le festività imminenti (oggi / domani / dopodomani), i
 * compleanni dei clienti E i task pianificati che sono "scattati",
 * proponendogli di mandare i messaggi.
 *
 * Priorità: scheduled-task ready > festività > compleanno cliente.
 *
 * iter v28 r386 — supporto compleanni
 * iter v28 r387 — supporto scheduled tasks (Fase B scheduler vocale)
 */
export default function HolidayPromptPopup() {
  const [scheduledTask, setScheduledTask] = useState(null);
  const [holiday, setHoliday] = useState(null);
  const [birthday, setBirthday] = useState(null);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const urlParams = new URLSearchParams(window.location.search);
    const previewDays = parseInt(urlParams.get("preview_holiday") || "0", 10);
    const holidayUrl = previewDays > 0
      ? `/contacts/bulk-greetings/holiday-prompt?preview_days=${previewDays}`
      : "/contacts/bulk-greetings/holiday-prompt";

    // 1) PRIORITÀ: scheduled task READY (pianificato vocalmente, ora è il momento)
    api.get("/scheduled-tasks/ready-prompt")
      .then(async ({ data }) => {
        if (!mounted) return;
        if (data?.task) {
          setScheduledTask(data.task);
          setOpen(true);
          return;
        }
        // 2) Festività
        try {
          const hResp = await api.get(holidayUrl);
          if (!mounted) return;
          if (hResp?.data?.holiday) {
            setHoliday(hResp.data.holiday);
            setOpen(true);
            return;
          }
        } catch { /* silent */ }
        // 3) Compleanno cliente
        try {
          const bdResp = await api.get("/contacts/bulk-greetings/birthday-prompt");
          if (!mounted) return;
          if (bdResp?.data?.contact) {
            setBirthday(bdResp.data.contact);
            setOpen(true);
          }
        } catch { /* silent */ }
      })
      .catch(() => {
        /* niente popup se l'endpoint fallisce (utente non loggato, ecc.) */
      });
    return () => {
      mounted = false;
    };
  }, []);

  const dismiss = async () => {
    setOpen(false);
    if (scheduledTask) {
      try {
        await api.delete(`/scheduled-tasks/${scheduledTask.id}`);
      } catch { /* silent */ }
    } else if (holiday) {
      const year = parseInt(holiday.date.slice(0, 4), 10);
      try {
        await api.post("/contacts/bulk-greetings/holiday-dismiss", { key: holiday.key, year });
      } catch { /* silent */ }
    } else if (birthday) {
      try {
        await api.post("/contacts/bulk-greetings/birthday-dismiss", {
          contact_id: birthday.id,
          target_date: birthday.target_date,
        });
      } catch { /* silent */ }
    }
  };

  const send = async () => {
    setOpen(false);
    if (scheduledTask) {
      // Marca come eseguito → non riapparirà
      try {
        await api.post(`/scheduled-tasks/${scheduledTask.id}/execute`);
      } catch { /* silent */ }
    }
    setBulkOpen(true);
  };

  if (!scheduledTask && !holiday && !birthday) {
    return bulkOpen ? (
      <BulkGreetingsModal open={bulkOpen} onClose={() => setBulkOpen(false)} />
    ) : null;
  }

  // Modal pre-fill differenziato per sorgente.
  let prefillProps = {};
  if (scheduledTask) {
    if (scheduledTask.kind === "auguri") {
      prefillProps = {
        prefillOccasion: scheduledTask.payload?.occasion || "custom",
        prefillCustomText: scheduledTask.payload?.custom_text || "",
      };
    } else {
      // match / promemoria → apre il modal in modalità custom (l'agente
      // ha già le note vocali nel banner, può copiare/modificare).
      prefillProps = {
        prefillOccasion: "custom",
        prefillCustomText: scheduledTask.notes || "",
      };
    }
  } else if (holiday) {
    prefillProps = {
      prefillOccasion: holiday.key,
      prefillCustomText: holiday.default_message,
    };
  } else if (birthday) {
    prefillProps = {
      prefillOccasion: "compleanno_app",
      prefillCustomText: birthday.default_message,
      prefillContactIds: [birthday.id],
    };
  }

  // Display vars
  let headerEmoji = "🔔";
  let headerEyebrow = "";
  let headerTitle = "";
  let previewMessage = "";
  let ctaLabel = "Apriamo →";
  let dismissLabel = "Salta";

  if (scheduledTask) {
    const kindMeta = {
      auguri:     { emoji: "✨", title: "Auguri pianificati", cta: "Vediamo i messaggi" },
      match:      { emoji: "🏡", title: "Match annuncio pronto", cta: "Apri WhatsApp" },
      promemoria: { emoji: "💬", title: "Promemoria pronto", cta: "Vediamo" },
    };
    const meta = kindMeta[scheduledTask.kind] || kindMeta.promemoria;
    headerEmoji = meta.emoji;
    headerEyebrow = `Pronto ora · Pianificato`;
    headerTitle = meta.title;
    previewMessage = scheduledTask.notes || "—";
    ctaLabel = meta.cta;
    dismissLabel = "Annulla task";
  } else if (holiday) {
    const daysUntil = holiday.days_until;
    const whenLabel = daysUntil === 0 ? "Oggi" : daysUntil === 1 ? "Domani" : "Dopodomani";
    headerEmoji = holiday.emoji;
    headerEyebrow = `${whenLabel} · ${holiday.name}`;
    headerTitle = "Vuoi fare gli auguri ai tuoi clienti?";
    previewMessage = (holiday.default_message || "").replace(/\{\{nome\}\}/g, "Sig. Ferrari");
    ctaLabel = "Vediamo i messaggi";
    dismissLabel = "Non quest'anno";
  } else if (birthday) {
    const daysUntil = birthday.days_until;
    const whenLabel = daysUntil === 0 ? "Oggi" : daysUntil === 1 ? "Domani" : "Dopodomani";
    headerEmoji = "🎂";
    headerEyebrow = `${whenLabel} · Compleanno`;
    headerTitle = `${birthday?.name?.split(" ")[0] || "Un cliente"} compie gli anni`;
    const cognome = birthday?.name ? birthday.name.split(" ").slice(-1)[0] : "Ferrari";
    previewMessage = (birthday?.default_message || "")
      .replace(/Sig\.ra? \w+/, `Sig. ${cognome}`);
    ctaLabel = "Manda gli auguri";
    dismissLabel = "Salta";
  }

  return (
    <>
      <BulkGreetingsModal open={bulkOpen} onClose={() => setBulkOpen(false)} {...prefillProps} />
      {open && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[340px] max-w-[calc(100vw-32px)] bg-white border border-[#7C8B6E]/35 rounded-2xl shadow-xl overflow-hidden animate-slide-up"
          data-testid="holiday-popup"
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="text-[28px] leading-none shrink-0" aria-hidden="true">
                {headerEmoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E] mb-0.5">
                  {headerEyebrow}
                </div>
                <div className="text-[14px] font-semibold text-[#1C1C1A] leading-tight">
                  {headerTitle}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Chiudi"
                className="text-[#9A968B] hover:text-[#1C1C1A] -mr-1 -mt-1"
                data-testid="holiday-popup-close"
              >
                <X size={14} weight="bold" />
              </button>
            </div>

            {/* Anteprima messaggio o nota originale del task */}
            <div className="mt-3 bg-[#F1F4ED]/60 border-l-2 border-[#7C8B6E] rounded-r-md px-3 py-2.5">
              <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#7C8B6E] mb-1">
                {scheduledTask ? "Avevi detto" : holiday ? "Esempio per Sig. Ferrari" : "Anteprima messaggio"}
              </div>
              <p className="text-[11.5px] text-[#1C1C1A] leading-snug whitespace-pre-wrap">
                {previewMessage}
              </p>
            </div>

            <div className="text-[10.5px] text-[#9A968B] mt-2 leading-snug">
              {scheduledTask?.kind === "auguri" && "Pronto personalizza il messaggio per ogni contatto. Puoi modificare prima di inviare."}
              {scheduledTask?.kind === "match" && "Pronto apre il match coi lead interessati. Mandi tu i messaggi WhatsApp."}
              {scheduledTask?.kind === "promemoria" && "Pronto apre la chat WhatsApp con il messaggio pronto."}
              {!scheduledTask && holiday && "Pronto personalizza il messaggio per ogni contatto. Puoi modificarlo prima di inviare."}
              {!scheduledTask && birthday && "Pronto apre WhatsApp con il messaggio pronto. Puoi modificarlo prima di inviare."}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={send}
                className="inline-flex items-center gap-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12px] font-semibold px-3 py-1.5 rounded-lg shadow-sm"
                data-testid="holiday-popup-send"
              >
                <Sparkle size={11} weight="fill" />
                {ctaLabel}
                <ArrowRight size={10} weight="bold" />
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="text-[11px] text-[#9A968B] hover:text-[#1C1C1A] font-medium"
                data-testid="holiday-popup-dismiss"
              >
                {dismissLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
